import { useEffect, useState } from "react";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import { Grid } from "@mui/material";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Snackbar, { SnackbarOrigin } from '@mui/material/Snackbar';
import { Alert } from "@mui/material";
import api from "../../api";
import { updateAgencyBrandExperience } from "../../redux/reducers/userReducer";
import { connect, useDispatch } from "react-redux";
import FormItem from "./FormItem";
import SingleSelectDropdown from "./SingleSelectDropdown";
import MultiSelectDropdown from "../../shared/MultiSelectDropdown";


const priceOptions = [
    { id: 1, value: "€0-100", label: "€0 - €100" },
    { id: 2, value: "€100-500", label: "€100 - €500" },
    { id: 3, value: "€500+", label: "€500+" },
];

const BrandExperienceSection = ({ user }) => {
    console.log(user);
    const dispatch = useDispatch();
    const [currentBrands, setCurrentBrands] = useState([{ name: "", achievements: "", sales: { num_dealers: "", price_range: null } }]);
    const [pastBrands, setPastBrands] = useState([{ name: "", achievements: "", sales: { num_dealers: "", price_range: null } }]);
    const [sbState, setSBState] = useState({
        open: false,
    });
    const { open } = sbState;

    useEffect( () => {
        function setupCurrentBrands() {
            if ( user.company.brand_experience?.current_brands.length ) {
                setCurrentBrands( user.company.brand_experience.current_brands );
            }
        }

        function setupPastBrands() {
            if ( user.company.brand_experience?.past_brands.length ) {
                setPastBrands( user.company.brand_experience.past_brands );
            }
        }

        setupCurrentBrands();
        setupPastBrands();
    }, [] );

    const handleBrandChange = (index: number, field: string, value: string, type: string) => {
        const updatedBrands = type === "current" ? [...currentBrands] : [...pastBrands];
        
        if (field === "num_dealers" || field === "price_range") {
            updatedBrands[index] = { 
                ...updatedBrands[index], 
                sales: { ...updatedBrands[index].sales, [field]: value }
            };
        } else {
            updatedBrands[index] = { ...updatedBrands[index], [field]: value };
        }
        type === "current" ? setCurrentBrands(updatedBrands) : setPastBrands(updatedBrands);
    };

    const handleAddBrand = (type) => {
        const newBrand = { name: "", achievements: "", sales: { num_dealers: "", price_range: null } };
        type === "current" ? setCurrentBrands([...currentBrands, newBrand]) : setPastBrands([...pastBrands, newBrand]);
    };

    const handleUpdateExperience = () => {
        api.updateAgencyBrandExperience(
            user.company.id,
            {
                current_brands  : currentBrands,
                past_brands     : pastBrands
            },
            ( res ) => {
                if ( res.status === 200 ) {
                    dispatch( updateAgencyBrandExperience( res.data.brand_experience ) );
                    openSuccessSnackbar({ vertical: 'bottom', horizontal: 'center' });
                }
            }
        );
    };

    const openSuccessSnackbar = (position: SnackbarOrigin) => {
        setSBState({ ...position, open: true });
    };

    const handleCloseSB = () => {
        setSBState({ ...sbState, open: false });
    };

    const getObject = (needle, haystack) => {
        for(var i in haystack) {
            if(haystack[i].value == needle) return haystack[i];
        }
        return null;
    }

    return (
        <>
            <div style={{ marginTop: 26 }} className="w-14"></div>
            <div className="space-y-8">
                <div>
                    <h2 className="text-2xl font-semibold">Brands You Currently Work With</h2>
                    <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
                        List the brands you are currently collaborating with, including achievements.
                    </span>
                </div>

                {currentBrands.map((brand, index) => (
                    <div key={`current-brand-${index}`}>
                        <Input
                            placeholder="Brand Name"
                            value={brand.name}
                            onChange={(e) => handleBrandChange(index, "name", e.target.value, "current")}
                            style={{ marginBottom: "16px" }}
                        />
                        <Textarea
                            placeholder="Realizations & Achievements"
                            rows={4}
                            value={brand.achievements}
                            onChange={(e) => handleBrandChange(index, "achievements", e.target.value, "current")}
                        />
                        <Grid container spacing={2} style={{ marginTop: 16 }}>
                            <Grid item xs={6}>
                                <FormItem label="Number of Dealers">
                                    <Input
                                        type="number"
                                        value={brand.sales.num_dealers}
                                        onChange={(e) => handleBrandChange(index, "num_dealers", e.target.value, "current")}
                                    />
                                </FormItem>
                            </Grid>
                            <Grid item xs={6}>
                                <FormItem label="Price Range (€)">
                                    <MultiSelectDropdown
                                        singleSelect
                                        options={priceOptions}
                                        onChange={(selected) => handleBrandChange(index, "price_range", selected?.value, "current")}
                                        value={getObject(brand?.sales?.price_range, priceOptions)}
                                    />
                                </FormItem>
                            </Grid>
                        </Grid>
                    </div>
                ))}
                <ButtonPrimary onClick={() => handleAddBrand("current")}>Add Another Current Brand</ButtonPrimary>

                <div>
                    <h2 className="text-2xl font-semibold mt-8">Brands You Worked With in the Past</h2>
                    <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
                        List the brands you have worked with in the past, including achievements.
                    </span>
                </div>

                {pastBrands.map((brand, index) => { 
                    return (
                    <div key={`past-brand-${index}`}>
                        <Input
                            placeholder="Brand Name"
                            value={brand.name}
                            onChange={(e) => handleBrandChange(index, "name", e.target.value, "past")}
                            style={{ marginBottom: "16px" }}
                        />
                        <Textarea
                            placeholder="Realizations & Achievements"
                            rows={4}
                            value={brand.achievements}
                            onChange={(e) => handleBrandChange(index, "achievements", e.target.value, "past")}
                        />
                        <Grid container spacing={2} style={{ marginTop: 16 }}>
                            <Grid item xs={6}>
                                <FormItem label="Number of Dealers">
                                    <Input
                                        type="number"
                                        value={brand.sales.num_dealers}
                                        onChange={(e) => handleBrandChange(index, "num_dealers", e.target.value, "past")}
                                    />
                                </FormItem>
                            </Grid>
                            <Grid item xs={6}>
                                <FormItem label="Price Range (€)">
                                    <MultiSelectDropdown
                                        singleSelect
                                        options={priceOptions}
                                        onChange={(selected) => handleBrandChange(index, "price_range", selected?.value, "past")}
                                        value={getObject(brand?.sales?.price_range, priceOptions)}
                                    />
                                </FormItem>
                            </Grid>
                        </Grid>
                    </div>
                )})}
                <ButtonPrimary onClick={() => handleAddBrand("past")}>Add Another Past Brand</ButtonPrimary>  
            
                <Grid container direction={'row'} className={'fullWidth'}>
                    <div className={'flex-1'}></div>
                    <ButtonPrimary onClick={handleUpdateExperience}>Save Changes</ButtonPrimary>
                </Grid>
            </div>

            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={open}
                autoHideDuration={5000}
                onClose={handleCloseSB}
                message="Experience Updated"
            >
                <Alert
                    onClose={handleCloseSB}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Brand Experience Updated!
                </Alert>
            </Snackbar>
        </>
    );
};

const mapStateToProps = ( state ) => ({
    user    : state.user.user,
});

export default connect( mapStateToProps, { updateAgencyBrandExperience } )( BrandExperienceSection );
